import qs from 'qs';
import { useState, type VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';
import type Stripe from 'stripe';

import { isDowngradingBusinessTier } from '../../../../../shared/subscriptions/utils';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { useRouter } from '../../../../contexts/RouterContext';
import { useAddToast } from '../../../../contexts/ToastContext';
import { window } from '../../../../globals';
import { LONG_DATE_FORMAT } from '../../../../helpers/date';
import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import { useOnMount } from '../../../../hooks';
import { useSynchronizeSubscription } from '../../../../hooks/api/subscriptions/useSynchronizeSubscription';
import { useUpdatePlan } from '../../../../hooks/api/subscriptions/useUpdatePlan';
import { Icon } from '../../../generic';
import { BUTTON_SIZE, BUTTON_TYPE, LoaderButton } from '../../../generic/Button';
import { RangeStepper } from '../../../generic/Forms';
import Loader from '../../../generic/Loader';
import FeaturesComponent from '../Features';
import LimitsComponent from '../Limits';
import { formatPrice, generateSteps, getDynamicPlanName, isBusinessPlan } from '../utils';
import type { FossaSubscriptionPlan } from '../../../../types';

import { usePlansContext } from './PlansContext';
import type { ViewIntervalOptions } from '.';

import styles from './styles.module.less';

type GetButtonParams = {
  currentPlan: FossaSubscriptionPlan,
  selectedPrice: Stripe.Price;
};

const START_TRIAL_CONFIRMATION_MODAL_OPTIONS = {
  body: (
    <div className={styles.confirmTrialModal}>
      <p>Here are some of the benefits you&apos;ll get:</p>
      <ul>
        <li><Icon icon="fa-solid fa-check" /> 200 Projects/SBOM imports</li>
        <li><Icon icon="fa-solid fa-check" /> 100 Contributors</li>
        <li><Icon icon="fa-solid fa-check" /> 10 Release Groups</li>
        <li><Icon icon="fa-solid fa-check" /> Unlimited dependency depth for scans</li>
        <li><Icon icon="fa-solid fa-check" /> Fully unlock quality checks</li>
        <li><Icon icon="fa-solid fa-check" /> No credit card required to start</li>
        <li><Icon icon="fa-solid fa-check" /> Convert to paid plan at anytime during the trial</li>
      </ul>
      <p>At the end of your 14-day trial, if you have a payment method on file, you will be charged.</p>
    </div>
  ),
  cancelText: 'Close',
  confirmBtnType: BUTTON_TYPE.PRIMARY,
  confirmText: 'Start Trial',
  title: 'You are about to start our 14-day Business XL Trial for free',
};

function getButtonText({
  currentPlan,
  selectedPrice,
}: GetButtonParams): string {
  const isSelectedCurrent = currentPlan.plan === selectedPrice.id;
  const isSelectedNext = currentPlan.nextScheduledPrice?.id === selectedPrice.id;

  if (currentPlan.trialDaysRemaining && isSelectedCurrent) {
    return `Trial in Progress - ${currentPlan.trialDaysRemaining} days remaining`;
  }

  if (isSelectedNext && currentPlan.endDate) {
    return `Your subscription starts on ${LONG_DATE_FORMAT.format(new Date(currentPlan.endDate * 1000))}`;
  }

  if (currentPlan?.nextScheduledPrice?.id && isSelectedCurrent && currentPlan.endDate) {
    return `Your subscription ends on ${LONG_DATE_FORMAT.format(new Date(currentPlan.endDate * 1000))}`;
  }

  if (isSelectedCurrent) {
    return 'Current Plan';
  }

  if (isOrgInFreeTier()) {
    if (!currentPlan.hasUsedFreeTrial) {
      return 'Start Free Trial';
    }

    return 'Upgrade plan';
  }

  return 'Change Plan';
}

function getConfirmText(currentViewInterval: ViewIntervalOptions, newViewInterval: ViewIntervalOptions) {
  if (currentViewInterval === 'year' && newViewInterval === 'year') {
    return {
      title: 'You will get a refund',
      body: 'You will be refunded for any remaining time on your current annual subscription and immediately billed for the new subscription tier.',
    };
  }

  return {
    title: 'You will be prorated with a credit on the next bill cycle',
    body: 'You will be immediately billed for the higher subscription tier and then prorated with a credit on the next bill cycle for the remaining time on the previous subscription.',
  };
}

function hasUpdatedPlanParam(): boolean {
  const params = qs.parse(window.location.search.substring(1));
  return !!params?.update_success;
}

type MessageModalProps = {
  onHide: () => void;
};

const MessageModal: VoidFunctionComponent<MessageModalProps> = ({
  onHide,
}: MessageModalProps) => {
  const {
    isLoading,
    mutate: synchronizeSubscription,
  } = useSynchronizeSubscription();

  const {
    currentPlan,
    isLoading: isLoadingCurrentPlan,
  } = usePlansContext();

  const router = useRouter();

  useOnMount(synchronizeSubscription);

  const handleHide = () => {
    router.replace({ pathname: window.location.pathname, search: '?tab=Plans' });
    window.location.reload();
    onHide();
  };

  let title = `You upgraded your plan`;
  let text = `You are now subscribed to our ${getDynamicPlanName(currentPlan?.price?.product as Stripe.Product)} Plan.`;

  const currentPlanEndDate = currentPlan?.endDate;
  if (currentPlan?.nextScheduledPrice?.id && currentPlanEndDate) {
    title = `You downgraded your plan`;
    text +=
      ' Your account will be downgraded at the end of your current billing period' +
      ` on ${LONG_DATE_FORMAT.format(new Date(currentPlanEndDate * 1000))}.`;
  }

  return (
    <Modal show onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title className={styles.modalUpdatePlanTitle}>
          {
            isLoading || isLoadingCurrentPlan
              ? 'Updating...'
              : title
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalUpdatePlanBody}>
        {
          isLoading || isLoadingCurrentPlan
            ? <Loader />
            : <p>{text}</p>
        }
      </Modal.Body>
    </Modal>
  );
};

/**
 * Renders the price component specifically for the BUSINESS plan.
 * Displays a list of available prices, allowing users to choose a price level and view relevant
 * features and usage limits for the selected tier.
 */
const DisplayBusinessPlan: VoidFunctionComponent = () => {
  const {
    currentPlan,
    selectedBusinessPrice: selectedPrice,
    viewInterval,
  } = usePlansContext();

  const {
    data: updateResult,
    isLoading: isUpdating,
    mutateAsync: updatePlan,
    isSuccess: hasUpdatedPlan,
  } = useUpdatePlan();

  const [
    shouldShowModal,
    setShouldShowModal,
  ] = useState<boolean>(hasUpdatedPlanParam() || hasUpdatedPlan);

  const { onConfirm } = useDialogContext();

  const addToast = useAddToast();

  if (!selectedPrice || !currentPlan) {
    return null;
  }

  const isSelectedCurrent = currentPlan.plan === selectedPrice.id;
  const isSelectedNext = currentPlan?.nextScheduledPrice?.id === selectedPrice.id;

  if (updateResult?.sessionUrl) {
    // Redirects to Stripe checkout if there's a session URL
    window.location = updateResult.sessionUrl;
  }

  const executeUpdate = async () => {
    try {
      const updatePlanResponse = await updatePlan(selectedPrice.id);
      if (updatePlanResponse?.sessionUrl) {
        // User is going to checkout, do not show modal
        return;
      }
      setShouldShowModal(true);
    } catch (e) {
      addToast({ type: 'error', content: `An unexpected error happened. Error message: ${(e as Error).message}` });
    }
  };

  const onClickAfterConfirmation = () => {
    const currentProduct = currentPlan?.price?.product as Stripe.Product;
    const selectedProduct = selectedPrice?.product as Stripe.Product;

    // If the user is downgrading business tiers, or coming from a non-business plan
    // we don't want to show anything, just executed
    if (
      !currentProduct
      || isDowngradingBusinessTier(currentProduct?.name, selectedProduct.name)
      || !isBusinessPlan(currentProduct)
    ) {
      executeUpdate();
      return;
    }

    // Show info modal when user is upgrading
    onConfirm({
      ...getConfirmText(currentPlan?.price?.recurring?.interval as ViewIntervalOptions, viewInterval),
      confirmText: 'Proceed with Upgrade',
      confirmBtnType: BUTTON_TYPE.PRIMARY,
      onConfirm: executeUpdate,
    });
  };

  const onClick = () => {
    if (isOrgInFreeTier() && !currentPlan.hasUsedFreeTrial) {
      // If starting a trial, we want to show a modal with more info
      onConfirm({
        ...START_TRIAL_CONFIRMATION_MODAL_OPTIONS,
        onConfirm: onClickAfterConfirmation,
      });
      return;
    }

    onClickAfterConfirmation();
  };

  return (
    <>
      {
        shouldShowModal &&
        <MessageModal
          onHide={() => setShouldShowModal(false)}
        />
      }
      <DisplayDiscount />
      <span className={styles.price}>{formatPrice(selectedPrice.unit_amount || 0, viewInterval)}</span>
      <DisplayBusinessPricesSlider />
      <LoaderButton
        size={BUTTON_SIZE.LARGE}
        type={isSelectedCurrent || isSelectedNext ? BUTTON_TYPE.SECONDARY : BUTTON_TYPE.PRIMARY}
        isFullWidth
        onClick={onClick}
        isDisabled={isSelectedCurrent || isSelectedNext}
        isLoading={isUpdating}
      >
        { getButtonText({
          currentPlan,
          selectedPrice,
        }) }
      </LoaderButton>
      <LimitsComponent product={selectedPrice.product as Stripe.Product} />
      <FeaturesComponent product={selectedPrice.product as Stripe.Product} />
    </>
  );
};

export const DisplayBusinessPricesSlider: VoidFunctionComponent = () => {
  const {
    selectedBusinessPrice: selectedPrice,
    filteredBusinessPrices: filteredPrices,
    handleBusinessPriceChange: handlePriceChange,
  } = usePlansContext();

  if (!selectedPrice) {
    return null;
  }
  return (
    <div className={styles.priceSliderContainer}>
      <RangeStepper
        id="business-price-range-stepper"
        steps={generateSteps(filteredPrices)}
        onChange={handlePriceChange}
        defaultStepValue={selectedPrice.id}
      />
    </div>
  );
};

const DisplayDiscount: VoidFunctionComponent = () => {
  const {
    businessDiscount: discount,
  } = usePlansContext();

  if (!discount) {
    return null;
  }
  return (
    <div className={styles.discount}>
      {discount}% discount
    </div>
  );
};

export default DisplayBusinessPlan;
